import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getUserSites, createSite as createSiteAPI, updateSite as updateSiteAPI, deleteSite as deleteSiteAPI } from '../services/api/siteService';
import { getUserUnits, getSiteUnits as getSiteUnitsAPI, createUnit as createUnitAPI, updateUnit as updateUnitAPI, deleteUnit as deleteUnitAPI } from '../services/api/unitService';

const SiteUnitContext = createContext();

export const useSiteUnit = () => {
    const context = useContext(SiteUnitContext);
    if (!context) {
        throw new Error('useSiteUnit must be used within a SiteUnitProvider');
    }
    return context;
};

export const SiteUnitProvider = ({ children, userKey }) => {
      const activeRequestRef = useRef(0);
    const [selectedSite, setSelectedSite] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [sites, setSites] = useState([]);
    const [units, setUnits] = useState([]);
    const [allUnits, setAllUnits] = useState([]); // Always contains all units from all sites
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

      // Load sites for the currently authenticated user
      const loadUserSites = useCallback(async () => {
          const requestId = ++activeRequestRef.current;

          setLoading(true);
          setError(null);

          try {
              const response = await getUserSites();

              // Ignore responses started for another account.
              if (requestId !== activeRequestRef.current) {
                  return;
              }

              if (!response.success) {
                  throw new Error(
                      response.message || 'Failed to load sites'
                  );
              }

              const nextSites = Array.isArray(response.data)
                  ? response.data
                  : [];

              const nextAllUnits = nextSites.flatMap(
                  site => site.units || []
              );

              setSites(nextSites);
              setAllUnits(nextAllUnits);
              setSelectedUnit(null);

              if (nextSites.length > 0) {
                  setSelectedSite(nextSites[0]);
                  setUnits(nextSites[0].units || []);
              } else {
                  setSelectedSite(null);
                  setUnits([]);
              }
          } catch (error) {
              if (requestId !== activeRequestRef.current) {
                  return;
              }

              console.error('Error loading sites:', error);

              // Never retain sites belonging to another account.
              setSites([]);
              setUnits([]);
              setAllUnits([]);
              setSelectedSite(null);
              setSelectedUnit(null);
              setError('Failed to load sites');
          } finally {
              if (requestId === activeRequestRef.current) {
                  setLoading(false);
              }
          }
      }, []);

    // Load units for a specific site
    const loadSiteUnits = useCallback(async (siteId) => {
        if (!siteId) return;

        setLoading(true);
        setError(null);
        try {
            const response = await getSiteUnitsAPI(siteId);
            if (response.success) {
                setUnits(response.data);
            }
        } catch (error) {
            console.error('Error loading site units:', error);
            setError('Failed to load units');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load all units for the user
    const loadAllUnits = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getUserUnits();
            if (response.success) {
                setUnits(response.data);
            }
        } catch (error) {
            console.error('Error loading units:', error);
            setError('Failed to load units');
        } finally {
            setLoading(false);
        }
    }, []);

      // Clear and reload data whenever the account changes
      React.useEffect(() => {
          // Invalidate requests created for the previous account.
          activeRequestRef.current += 1;

          setSites([]);
          setUnits([]);
          setAllUnits([]);
          setSelectedSite(null);
          setSelectedUnit(null);
          setError(null);

          if (!userKey) {
              setLoading(false);
              return undefined;
          }

          loadUserSites();

          return () => {
              activeRequestRef.current += 1;
          };
      }, [userKey, loadUserSites]);

    const selectSite = useCallback((site) => {
        setSelectedSite(site);
        if (site) {
            setUnits(site.units || []);
            // Reset unit selection when site changes
            setSelectedUnit(null);
        } else {
            // "All Sites" selected - use all units
            setUnits(allUnits);
            setSelectedUnit(null);
        }
    }, [allUnits]);

    const selectUnit = useCallback((unit) => {
        setSelectedUnit(unit);
    }, []);

    const getUnitStatus = useCallback((unitId) => {
        const unit = units.find(u => u.id === unitId);
        return unit ? unit.status : 'Unknown';
    }, [units]);

    const getSiteUnits = useCallback((siteId) => {
        const site = sites.find(s => s.id === siteId);
        return site ? site.units || [] : [];
    }, [sites]);

    const getAllUnits = useCallback(() => {
        return allUnits; // Return the always-up-to-date allUnits array
    }, [allUnits]);

    const createSite = useCallback(async (siteData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await createSiteAPI(siteData);
            if (response.success) {
                const newSite = {
                    ...response.data,
                    units: [] // Initialize with empty units
                };
                setSites(prev => [...prev, newSite]);
                return newSite;
            }
            throw new Error(response.message || 'Failed to create site');
        } catch (error) {
            console.error('Error creating site:', error);
            setError('Failed to create site');
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteSite = useCallback(async (siteId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await deleteSiteAPI(siteId);
            if (response.success) {
                setSites(prev => prev.filter(site => site.id !== siteId));
                // Clear selection if deleted site was selected
                if (selectedSite && selectedSite.id === siteId) {
                    setSelectedSite(null);
                    setUnits([]);
                }
                return true;
            }
            throw new Error(response.message || 'Failed to delete site');
        } catch (error) {
            console.error('Error deleting site:', error);
            setError('Failed to delete site');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [selectedSite]);

    const updateSite = useCallback(async (siteId, updatedSiteData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await updateSiteAPI(siteId, updatedSiteData);
            if (response.success) {
                setSites(prev => prev.map(site =>
                    site.id === siteId
                        ? { ...site, ...response.data }
                        : site
                ));

                // Update selected site if it's the one being updated
                if (selectedSite && selectedSite.id === siteId) {
                    setSelectedSite({ ...selectedSite, ...response.data });
                }
                return response.data;
            }
            throw new Error(response.message || 'Failed to update site');
        } catch (error) {
            console.error('Error updating site:', error);
            setError('Failed to update site');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [selectedSite]);

    const getSiteById = useCallback((siteId) => {
        return sites.find(site => site.id === parseInt(siteId));
    }, [sites]);

    const addUnitToSite = useCallback(async (siteId, unitData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await createUnitAPI({ ...unitData, site_id: siteId });
            if (response.success) {
                // Update the sites array to include the new unit
                setSites(prev => prev.map(site =>
                    site.id === siteId
                        ? { ...site, units: [...(site.units || []), response.data] }
                        : site
                ));

                // Update all units array
                setAllUnits(prev => [...prev, response.data]);

                // If this site is currently selected, update units
                if (selectedSite && selectedSite.id === siteId) {
                    setUnits(prev => [...prev, response.data]);
                }

                return response.data;
            }
            throw new Error(response.message || 'Failed to create unit');
        } catch (error) {
            console.error('Error creating unit:', error);
            setError('Failed to create unit');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [selectedSite]);

    const removeUnitFromSite = useCallback(async (unitId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await deleteUnitAPI(unitId);
            if (response.success) {
                // Update the sites array to remove the unit
                setSites(prev => prev.map(site => ({
                    ...site,
                    units: (site.units || []).filter(unit => unit.id !== unitId)
                })));

                // Update all units array
                setAllUnits(prev => prev.filter(unit => unit.id !== unitId));

                // Update units if currently viewing units
                setUnits(prev => prev.filter(unit => unit.id !== unitId));

                // Clear selection if deleted unit was selected
                if (selectedUnit && selectedUnit.id === unitId) {
                    setSelectedUnit(null);
                }

                return true;
            }
            throw new Error(response.message || 'Failed to delete unit');
        } catch (error) {
            console.error('Error deleting unit:', error);
            setError('Failed to delete unit');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [selectedUnit]);

    const updateUnitData = useCallback(async (unitId, unitData) => {
        setLoading(true);
        setError(null);
        try {
            const response = await updateUnitAPI(unitId, unitData);
            if (response.success) {
                // Update the sites array
                setSites(prev => prev.map(site => ({
                    ...site,
                    units: (site.units || []).map(unit =>
                        unit.id === unitId ? { ...unit, ...response.data } : unit
                    )
                })));

                // Update all units array
                setAllUnits(prev => prev.map(unit =>
                    unit.id === unitId ? { ...unit, ...response.data } : unit
                ));

                // Update units if currently viewing units
                setUnits(prev => prev.map(unit =>
                    unit.id === unitId ? { ...unit, ...response.data } : unit
                ));

                // Update selected unit if it's the one being updated
                if (selectedUnit && selectedUnit.id === unitId) {
                    setSelectedUnit({ ...selectedUnit, ...response.data });
                }

                return response.data;
            }
            throw new Error(response.message || 'Failed to update unit');
        } catch (error) {
            console.error('Error updating unit:', error);
            setError('Failed to update unit');
            throw error;
        } finally {
            setLoading(false);
        }
    }, [selectedUnit]);

    const value = {
        selectedSite,
        selectedUnit,
        sites,
        units,
        allUnits, // Always contains all units from all sites
        loading,
        error,
        selectSite,
        selectUnit,
        getUnitStatus,
        getSiteUnits,
        getAllUnits,
        createSite,
        deleteSite,
        getSiteById,
        addUnitToSite,
        removeUnitFromSite,
        updateUnitData,
        updateSite,
        loadUserSites,
        loadSiteUnits,
        loadAllUnits
    };

    return (
        <SiteUnitContext.Provider value={value}>
            {children}
        </SiteUnitContext.Provider>
    );
};

export default SiteUnitContext;
